import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Typography, message } from 'antd';
import { SafetyCertificateOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import DSCModal from './DSCModal/DSCModal';
import dscService from '../services/dscService';
import './DSCModal/DSCModal.css';

const { Title, Text } = Typography;

const DSCTestPage = () => {
    const [showDSCModal, setShowDSCModal] = useState(false);
    const [signature, setSignature] = useState(null);
    const [debugInfo, setDebugInfo] = useState(null);
    const [helperRunning, setHelperRunning] = useState(null);
    const [checkingHelper, setCheckingHelper] = useState(false);

    const [testData] = useState({
        test: 'DSC Test Signing',
        time: new Date().toISOString()
    });

    const checkHelper = useCallback(async (silent = false) => {
        try {
            if (!silent) setCheckingHelper(true);

            const result = await dscService.checkLocalHelper();
            setHelperRunning(result.success);

            console.log('🩺 Helper check result:', result);

            if (!silent) {
                if (result.success) {
                    message.success('DSC Helper is running');
                } else {
                    message.warning('DSC Helper is not running');
                }
            }
        } catch (error) {
            console.error('Helper check failed:', error);
            setHelperRunning(false);

            if (!silent) {
                message.error('Unable to check DSC Helper status');
            }
        } finally {
            if (!silent) setCheckingHelper(false);
        }
    }, []);

    useEffect(() => {
        checkHelper(true);
        const interval = setInterval(() => checkHelper(true), 5000);
        return () => clearInterval(interval);
    }, [checkHelper]);

    const handleTestSign = () => {
        if (helperRunning === false) {
            message.warning('Please start DSC Helper first');
            return;
        }

        setSignature(null);
        setDebugInfo(null);
        setShowDSCModal(true);
    };

    const handleDSCSuccess = (result) => {
        console.log('✅ DSC Success callback received:', result);

        setSignature(result.signature);
        setDebugInfo({
            success: true,
            timestamp: new Date().toISOString(),
            signatureLength: result.signature?.length || 0,
            hasCertificate: !!result.certificate
        });

        setShowDSCModal(false);
        message.success('DSC signing successful!');
    };

    const handleDSCCancel = () => {
        console.log('❌ DSC cancelled by user');
        setShowDSCModal(false);
        setDebugInfo(null);
    };

    const handleDSCError = (error) => {
        console.error('❌ DSC Error:', error);
        setDebugInfo({
            success: false,
            error: error.message || 'Unknown error',
            timestamp: new Date().toISOString()
        });
    };

    const handleDownloadHelper = () => {
        window.location.href = 'https://dmarepositoryportal.in/downloads/DSCHelper.zip' ;
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                background: '#f0f2f5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px'
            }}
        >
            <Card
                style={{
                    maxWidth: '600px',
                    width: '100%',
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
            >
                <SafetyCertificateOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                <Title level={2}>DSC Test</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: '20px' }}>
                    Test your Digital Signature Certificate
                </Text>

                {helperRunning === false && (
                    <div
                        style={{
                            marginBottom: '20px',
                            padding: '16px',
                            background: '#fff7e6',
                            border: '1px solid #ffd591',
                            borderRadius: '8px',
                            textAlign: 'left'
                        }}
                    >
                        <Text strong style={{ color: '#fa8c16' }}>⚠️ DSC Helper Not Detected</Text>
                        <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                            Please start the DSC Helper application on this computer.
                        </div>
                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadHelper}
                                style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
                            >
                                Download DSC Helper
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => checkHelper(false)}
                                loading={checkingHelper}
                            >
                                Retry Detection
                            </Button>
                        </div>
                        <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                            After starting the helper, click Retry Detection.
                        </Text>
                    </div>
                )}

                {helperRunning === true && (
                    <div
                        style={{
                            marginBottom: '20px',
                            padding: '12px',
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: '8px'
                        }}
                    >
                        <Text style={{ color: '#52c41a' }}>✅ DSC Helper is running</Text>
                    </div>
                )}

                {helperRunning === null && (
                    <div
                        style={{
                            marginBottom: '20px',
                            padding: '12px',
                            background: '#e6f4ff',
                            border: '1px solid #91caff',
                            borderRadius: '8px'
                        }}
                    >
                        <Text style={{ color: '#1677ff' }}>⏳ Checking DSC Helper status...</Text>
                    </div>
                )}

                <Button
                    type="primary"
                    size="large"
                    onClick={handleTestSign}
                    icon={<SafetyCertificateOutlined />}
                    style={{ margin: '20px 0' }}
                    disabled={helperRunning !== true}
                >
                    Test DSC
                </Button>

                {debugInfo && (
                    <div
                        style={{
                            marginTop: '20px',
                            padding: '12px',
                            background: debugInfo.success ? '#f6ffed' : '#fff2f0',
                            border: debugInfo.success ? '1px solid #b7eb8f' : '1px solid #ffccc7',
                            borderRadius: '8px',
                            textAlign: 'left'
                        }}
                    >
                        <Text strong style={{ color: debugInfo.success ? '#52c41a' : '#ff4d4f' }}>
                            {debugInfo.success ? '✅ Success Info:' : '❌ Error Info:'}
                        </Text>
                        <div style={{ fontSize: '12px', marginTop: '8px', wordBreak: 'break-all' }}>
                            <div>📅 Time: {debugInfo.timestamp}</div>
                            {debugInfo.signatureLength ? (
                                <div>📏 Signature Length: {debugInfo.signatureLength} chars</div>
                            ) : null}
                            {debugInfo.hasCertificate ? (
                                <div>🔐 Certificate: Present</div>
                            ) : null}
                            {debugInfo.error ? (
                                <div style={{ color: '#ff4d4f' }}>⚠️ Error: {debugInfo.error}</div>
                            ) : null}
                        </div>
                    </div>
                )}

                {signature && (
                    <div
                        style={{
                            background: '#f6ffed',
                            padding: '12px',
                            borderRadius: '8px',
                            marginTop: '20px',
                            textAlign: 'left'
                        }}
                    >
                        <Text strong style={{ color: '#52c41a' }}>✅ Signature:</Text>
                        <Text
                            copyable
                            style={{
                                display: 'block',
                                fontSize: '11px',
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                                marginTop: '8px',
                                padding: '8px',
                                background: '#fff',
                                borderRadius: '4px',
                                maxHeight: '150px',
                                overflow: 'auto'
                            }}
                        >
                            {signature}
                        </Text>
                    </div>
                )}

                <DSCModal
                    visible={showDSCModal}
                    dataToSign={testData}
                    onSuccess={handleDSCSuccess}
                    onClose={handleDSCCancel}
                    onError={handleDSCError}
                />
            </Card>
        </div>
    );
};

export default DSCTestPage;